import { Mui } from "@osu/react-ui";
import { getApplDetailParams } from "../../util/functions";
import {
    useGetScholarsApplicantQueueEntriesQuery,
} from "../../services/graphql/api";
import { useApplicantDetailsState } from "../hooks/useApplicantDetailsState";

/**
 * Read-only Scholars Ratings view (Option A).
 *
 * Shown on the Scholars applicant details page once a recommendation has been submitted (the applicant's
 * queue entry is RECOMMENDED). Scholars is queue-based, so there is no Honors-style History section here;
 * this gives admins/PMs parity — the ability to see the Ratings a reviewer submitted after the fact.
 *
 * Data sources:
 *   - Submitted values: the RECOMMENDED queue entry's persisted `rubricValues` (AWSJSON) — the same map
 *     the reviewer graded, keyed by rubric item label -> choice key (plus a bookkeeping `rubricKey`).
 *   - Labels/choices: the active rubric config (configuration.rubric) rides along on the details blob,
 *     used to render item labels and translate a stored choice key to its human label.
 */
export default function ScholarsRatingsReadOnly() {
    const { applicantEmployeeId, term } = getApplDetailParams();
    const { data: queueEntries } = useGetScholarsApplicantQueueEntriesQuery(
        { applicantEmployeeId, term },
        { skip: !applicantEmployeeId }
    );
    const { data: appDetails } = useApplicantDetailsState();
    const rubricConfig = appDetails?.configuration?.rubric ?? {};

    // The submitted values live on the RECOMMENDED entry (the decided placement).
    const recommendedEntry = (queueEntries ?? []).find(e => e?.status === "RECOMMENDED");
    const stored = parseRubricValues(recommendedEntry?.rubricValues);

    if (!recommendedEntry) { return null; }

    const items = rubricConfig?.rubricItems ?? [];
    if (!(items.length > 0)) {
        return (
            <Mui.Typography sx={{ margin: "0 1rem" }} color="text.secondary">
                No ratings configuration is available to display these values.
            </Mui.Typography>
        );
    }

    return (
        <div style={{ margin: "0 1rem" }} data-testid="scholars-ratings-readonly">
            <Mui.Box sx={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
                {items.map(item => {
                    const storedChoiceKey = stored?.[item.label];
                    const choiceLabel = (item.choices ?? []).find(c => c.key === storedChoiceKey)?.label;
                    return (
                        <Mui.Box key={item.label} sx={{ minWidth: "12rem" }}>
                            <Mui.Typography sx={{ fontWeight: "bold" }}>{item.label}</Mui.Typography>
                            <Mui.Typography>{choiceLabel || "—"}</Mui.Typography>
                        </Mui.Box>
                    );
                })}
            </Mui.Box>
        </div>
    );
}

// The queue-entry rubricValues field is AWSJSON (a JSON string). Tolerate an already-parsed object.
function parseRubricValues(raw) {
    if (!raw) { return {}; }
    if (typeof raw === "object") { return raw; }
    try { return JSON.parse(raw); } catch (_e) { return {}; }
}
